import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaFileAlt, FaArrowRight, FaExternalLinkAlt, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const DownloadsOpus24Module1 = () => {
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [expandedReviewIndex, setExpandedReviewIndex] = useState(null);
  
  const toggleReview = (index) => {
    setExpandedReviewIndex(expandedReviewIndex === index ? null : index);
  };

  const reviews = [
    {
      version: "10.0.86",
      date: "10 de octubre de 2024",
      changes: [
        "Se revisó el diseño del reporte de \"Análisis de precios unitarios\" para ECUADOR. Se tiene soporte para desglosar los elementos compuestos.",
        "En reporteador, se revisó que el reporte de análisis de precio unitario se muestre correctamente."
      ]
    },
    {
      version: "10.0.73",
      date: "16 de febrero de 2024",
      changes: [
        "Al imprimir el reporte de Análisis de Precio Unitario de reporteador, se revisó que las cantidades de los componentes de segundo nivel desplieguen correctamente la cantidad final.",
        "Al recalcular, si una matriz tiene costo cero, y por lo tanto precio unitario de cero, se revisó que el sistema muestre correctamente el informe relacionado en la audiroría.",
        "Se optimizó el correcto desglose de las escalatorias."
      ]
    },
    {
      version: "10.0.58",
      date: "2 de febrero de 2024",
      changes: [
        "Se revisó el código de programación al transferir conceptos desde F5 y el funcionamiento del cursor después de ejecutar esta tarea.",
        "Se mejoraron los procesos involucrados al seleccionar todos los elementos de una vista e intentar arrastrar hacia otra obra.",
        "Se optimizó el funcionamiento de los vínculos en el gantt, así como su correcta visualización y edición.",
        "Se revisó el proceso de copiar y pegar un costo horario que no tiene piezas especiales, de una obra a otra.",
        "Se mejoraron los cálculos involucrados en el importe acumulado de programa de obra y su coherencia con el importe de presupuesto.",
        "Se optimizó la visualización de los diálogos y el MDI, para que se muestren en tamaño y disposición correcta.",
        "En programa de suministros, se puede copiar la vista para pegar en Excel.",
        "Al configurar el calendario del programa de obra, se mejoraron los procesos relacionados con actividades con distribuciones personalizadas.",
        "Se revisó la información publicada en el informe de auditoría: que se muestre correctamente sin duplicidades.",
        "En programa de obra, si el proyecto tiene fechas no sincronizadas con el programa, se revisó que al imprimir se muestren correctamente.",
        "Soporte de cantidades negativas en cantidades de concepto.",
        "En la impresión de programa de obra en días, se revisó que la barra ocupe hasta el último día del proyecto.",
        "Se actualizó la UMA de 2024.",
        "En desglose de matriz, al agregar insumos desde f5, ahora se enfoca el último renglón agregado para que al insertar otro, se coloque debajo del más reciente."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Cambios OPUS 24 Módulo 1 | Ecosoft"
        description="Consulte los últimos cambios y actualizaciones del Módulo 1 de OPUS 24: Presupuesto Programable."
        name="DownloadsOpus24Module1"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[350px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl font-bold mb-2">OPUS 24 - Módulo 1</h1>
            <h2 className="text-2xl font-semibold mb-4">Presupuesto Programable</h2>
            <p className="text-lg mb-6">
              Consulte los últimos cambios y actualizaciones del Módulo 1 de OPUS 24.
            </p>
            <div className="flex gap-4">
              <Link to="/soporte/descargas" className="inline-flex items-center text-white hover:underline">
                <FaArrowRight className="mr-2 rotate-180" /> Volver al Centro de Descargas
              </Link>
              <Link to="/soporte/descargas/opus24/m1/changelog" className="inline-flex items-center text-white hover:underline">
                <FaFileAlt className="mr-2" /> Ver historial completo de cambios
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
            <div className="flex items-center mb-6">
              <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                <FaInfoCircle className="text-[#0055B8] text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#1A2B39]">Información importante</h2>
                <p className="text-gray-600">Sobre OPUS 24 - Módulo 1</p>
              </div>
            </div>
            
            <div className="mb-6 text-gray-700">
              <p className="mb-4">
                Para descargar el programa OPUS 24, por favor visite directamente nuestro portal oficial de descargas en <a href="https://descargas.ecosoft.com.mx/" target="_blank" rel="noopener noreferrer" className="text-[#0055B8] font-medium hover:underline">descargas.ecosoft.com.mx</a>.
              </p>
              <p className="mb-4">
                OPUS se adquiere bajo un esquema de suscripción, con fecha de caducidad. Esto quiere decir que puedes trabajar con revisiones con fecha de publicación igual o anterior a la fecha de caducidad de tu suscripción.
              </p>
              <p>
                OPUS 24 es compatible con las últimas actualizaciones de Windows. Si la fecha de tu suscripción no incluye las versiones más recientes, te invitamos a actualizarte.
              </p>
            </div>
          </div>
          
          {/* Portal de Descargas iframe */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Portal de Descargas Oficial</h2>
            <iframe 
              src="https://descargas.ecosoft.com.mx/" 
              title="Portal de Descargas OPUS 24" 
              className="w-full h-[600px] border rounded shadow-lg mb-3"
              allow="fullscreen"
            />
            <p className="text-sm text-gray-600 text-center">
              Portal oficial para descargar OPUS 24 y todas sus actualizaciones.
            </p>
          </div>
          
          {/* Changes and Improvements Section */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Cambios y mejoras</h2>
          
          <div className="space-y-4 mb-12">
            {reviews.map((review, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg shadow">
                <div 
                  className="p-4 flex justify-between items-center cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleReview(index)}
                >
                  <div>
                    <h3 className="font-bold text-lg text-[#1A2B39]">Revisión {review.version}</h3>
                    <p className="text-gray-600">Fecha: {review.date}</p>
                  </div>
                  <div>
                    {expandedReviewIndex === index ? (
                      <FaChevronUp className="text-[#0055B8]" />
                    ) : (
                      <FaChevronDown className="text-[#0055B8]" />
                    )}
                  </div>
                </div>
                
                {expandedReviewIndex === index && (
                  <div className="p-4 pt-0 border-t border-gray-200">
                    <ul className="list-disc pl-6 space-y-1 text-gray-700 mt-3">
                      {review.changes.map((change, changeIndex) => (
                        <li key={changeIndex}>{change}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
            
            <div className="mt-6 text-center">
              <Link 
                to="/soporte/descargas/opus24/m1/changelog" 
                className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                <FaFileAlt className="mr-2" /> Ver historial completo de cambios
              </Link>
            </div>
          </div>
          
          {/* Additional Resources */}
          <h2 className="text-3xl font-bold mb-6 text-[#1A2B39]">Recursos adicionales</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Guías y manuales</h3>
              <p className="mb-4 text-gray-600">
                Descargue los manuales de OPUS 24 o solicite uno actualizado enviándonos un correo a soporte@ecosoft.com.mx desde su cuenta asociada a su suscripción vigente.
              </p>
              <div className="space-y-3 mt-4">
                <a 
                  href="/documents/Opus24/M1.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center text-[#0055B8] hover:underline"
                >
                  <FaFileAlt className="mr-2" /> Manual Módulo 1 (Presupuesto Programable)
                </a>
                <div>
                  <a 
                    href="/documents/Opus24/guia_llave_acceso.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center text-[#0055B8] hover:underline"
                  >
                    <FaFileAlt className="mr-2" /> Guía para Llave de Acceso
                  </a>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
              <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Soporte técnico</h3>
              <p className="mb-4 text-gray-600">
                Si necesitas ayuda con la instalación o uso de OPUS, ponte en contacto con nuestro equipo de soporte técnico.
              </p>
              <p className="text-[#0055B8] font-bold">
                WhatsApp Soporte: (55) 7965.8522
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DownloadsOpus24Module1; 